import React, { useState, useEffect } from 'react';
import api from '../config/api';
import './DealsViewer.css';

function DealsViewer() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filtros
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [domainSearch, setDomainSearch] = useState('');
  const [sortBy, setSortBy] = useState('-pipedriveUpdatedAt');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalDeals, setTotalDeals] = useState(0);

  // Deal seleccionado para modal
  const [selectedDeal, setSelectedDeal] = useState(null);

  useEffect(() => {
    fetchDeals();
    fetchStats();
  }, [statusFilter, sortBy, currentPage]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1); // Reset to page 1 when search changes
      } else {
        fetchDeals(); // Fetch directly if already on page 1
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error obteniendo estadísticas:', err);
    }
  };

  const searchByDomain = async (domain) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/deals/by-domain/${encodeURIComponent(domain)}`);
      setDeals([response.data.data]);
      setTotalDeals(1);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(`No se encontró ningún deal con el dominio "${domain}"`);
        setDeals([]);
        setTotalDeals(0);
      } else {
        setError('Error buscando por dominio');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDeals = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        sort: sortBy
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await api.get('/api/deals', { params });
      setDeals(response.data.data);
      setTotalDeals(response.data.pagination.total);
    } catch (err) {
      setError('Error cargando deals');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByDomain = () => {
    if (domainSearch.trim()) {
      searchByDomain(domainSearch.trim());
    } else {
      fetchDeals();
    }
  };

  const totalPages = Math.ceil(totalDeals / itemsPerPage);

  const formatCurrency = (value, currency = 'USD') => {
    if (!value) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { label: 'Abierto', class: 'status-open' },
      won: { label: 'Ganado', class: 'status-won' },
      lost: { label: 'Perdido', class: 'status-lost' },
      deleted: { label: 'Eliminado', class: 'status-deleted' }
    };
    const badge = badges[status] || { label: status, class: '' };
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  return (
    <div className="deals-viewer">
      {/* Estadísticas */}
      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-label">Total</span>
            <span className="stat-value">{stats.deals.total}</span>
          </div>
          <div className="stat-item stat-open">
            <span className="stat-label">Abiertos</span>
            <span className="stat-value">{stats.deals.open}</span>
          </div>
          <div className="stat-item stat-won">
            <span className="stat-label">Ganados</span>
            <span className="stat-value">{stats.deals.won}</span>
          </div>
          <div className="stat-item stat-lost">
            <span className="stat-label">Perdidos</span>
            <span className="stat-value">{stats.deals.lost}</span>
          </div>
        </div>
      )}

      {/* Controles */}
      <div className="controls-bar">
        <div className="control-group">
          <input
            type="text"
            placeholder="Buscar por título del deal, nombre de persona, DNI o CUIL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="control-group" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Buscar por dominio (ej: ABC123)"
            value={domainSearch}
            onChange={(e) => setDomainSearch(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchByDomain();
              }
            }}
            className="search-input"
            style={{ maxWidth: '250px' }}
          />
          <button
            onClick={handleSearchByDomain}
            className="btn-search"
            title="Buscar por dominio"
          >
            🔍 Buscar
          </button>
          {domainSearch && (
            <button
              onClick={() => {
                setDomainSearch('');
                fetchDeals();
              }}
              className="btn-clear"
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>

        <div className="control-group">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">Todos los estados</option>
            <option value="open">Abiertos</option>
            <option value="won">Ganados</option>
            <option value="lost">Perdidos</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="-pipedriveUpdatedAt">Más recientes</option>
            <option value="pipedriveUpdatedAt">Más antiguos</option>
            <option value="-value">Mayor valor</option>
            <option value="value">Menor valor</option>
            <option value="title">Título A-Z</option>
            <option value="-title">Título Z-A</option>
          </select>

          <button onClick={fetchDeals} className="btn-refresh" title="Recargar">
            🔄
          </button>
        </div>
      </div>

      {/* Lista de Deals */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando deals...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchDeals} className="btn btn-secondary">
            Reintentar
          </button>
        </div>
      ) : deals.length === 0 ? (
        <div className="empty-state">
          <p>No se encontraron deals</p>
        </div>
      ) : (
        <>
          <div className="deals-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Título</th>
                  <th>Valor</th>
                  <th>Estado</th>
                  <th>Persona</th>
                  <th>Organización</th>
                  <th>Stage</th>
                  <th>Actualizado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => (
                  <tr key={deal._id}>
                    <td className="deal-id">{deal.pipedriveId}</td>
                    <td className="deal-title">{deal.title}</td>
                    <td className="deal-value">
                      {formatCurrency(deal.value, deal.currency)}
                    </td>
                    <td>{getStatusBadge(deal.status)}</td>
                    <td className="deal-person">{deal.personName || '-'}</td>
                    <td className="deal-org">{deal.orgName || '-'}</td>
                    <td className="deal-stage">{deal.stage?.name || '-'}</td>
                    <td className="deal-date">
                      {formatDate(deal.pipedriveUpdatedAt)}
                    </td>
                    <td>
                      <button
                        onClick={() => setSelectedDeal(deal)}
                        className="btn-view"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              ← Anterior
            </button>

            <span className="page-info">
              Página {currentPage} de {totalPages} ({totalDeals} deals)
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Siguiente →
            </button>
          </div>
        </>
      )}

      {/* Modal de detalle */}
      {selectedDeal && (
        <div className="modal-overlay" onClick={() => setSelectedDeal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedDeal.title}</h2>
              <button onClick={() => setSelectedDeal(null)} className="btn-close">
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>ID Pipedrive:</strong>
                  <span>{selectedDeal.pipedriveId}</span>
                </div>
                <div className="detail-item">
                  <strong>Estado:</strong>
                  {getStatusBadge(selectedDeal.status)}
                </div>
                <div className="detail-item">
                  <strong>Valor:</strong>
                  <span>{formatCurrency(selectedDeal.value, selectedDeal.currency)}</span>
                </div>
                <div className="detail-item">
                  <strong>Probabilidad:</strong>
                  <span>{selectedDeal.probability ? `${selectedDeal.probability}%` : '-'}</span>
                </div>
                <div className="detail-item">
                  <strong>Persona:</strong>
                  <span>{selectedDeal.personName || '-'}</span>
                </div>
                <div className="detail-item">
                  <strong>Organización:</strong>
                  <span>{selectedDeal.orgName || '-'}</span>
                </div>
                <div className="detail-item">
                  <strong>Pipeline:</strong>
                  <span>{selectedDeal.pipeline?.name || '-'}</span>
                </div>
                <div className="detail-item">
                  <strong>Stage:</strong>
                  <span>{selectedDeal.stage?.name || '-'}</span>
                </div>
                <div className="detail-item">
                  <strong>Propietario:</strong>
                  <span>{selectedDeal.ownerName || '-'}</span>
                </div>
                <div className="detail-item">
                  <strong>Fecha esperada de cierre:</strong>
                  <span>{formatDate(selectedDeal.expectedCloseDate)}</span>
                </div>
                <div className="detail-item">
                  <strong>Creado:</strong>
                  <span>{formatDate(selectedDeal.pipedriveAddedAt)}</span>
                </div>
                <div className="detail-item">
                  <strong>Actualizado:</strong>
                  <span>{formatDate(selectedDeal.pipedriveUpdatedAt)}</span>
                </div>
              </div>

              {selectedDeal.lostReason && (
                <div className="detail-section">
                  <strong>Razón de pérdida:</strong>
                  <p>{selectedDeal.lostReason}</p>
                </div>
              )}

              <div className="detail-section">
                <strong>Métricas:</strong>
                <div className="metrics-grid">
                  <div className="metric">
                    <span>Actividades:</span>
                    <strong>{selectedDeal.activitiesCount || 0}</strong>
                  </div>
                  <div className="metric">
                    <span>Emails:</span>
                    <strong>{selectedDeal.emailMessagesCount || 0}</strong>
                  </div>
                  <div className="metric">
                    <span>Notas:</span>
                    <strong>{selectedDeal.notesCount || 0}</strong>
                  </div>
                  <div className="metric">
                    <span>Archivos:</span>
                    <strong>{selectedDeal.filesCount || 0}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DealsViewer;
